import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { authSession } from "./authSession";
import { resolveApiBaseUrl } from "./apiHosts";
import {
  ensureAccessTokenFresh,
  logoutSession,
  refreshAccessToken,
} from "./sessionRefresh";

const prepareHeaders = (headers: Headers) => {
  const token = authSession.getAccessToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
};

const queryByBaseUrl = new Map<
  string,
  ReturnType<typeof fetchBaseQuery>
>();

const getBaseQueryForUrl = (baseUrl: string) => {
  const existing = queryByBaseUrl.get(baseUrl);
  if (existing) return existing;
  const created = fetchBaseQuery({
    baseUrl,
    prepareHeaders,
  });
  queryByBaseUrl.set(baseUrl, created);
  return created;
};

const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const url = typeof args === "string" ? args : (args.url ?? "");
  const baseUrl = resolveApiBaseUrl(url);
  if (!baseUrl) {
    return {
      error: {
        status: "CUSTOM_ERROR",
        error: "Regional API URL is not available. Sign in again.",
        data: { error: { code: "REGION_API_MISSING" } },
      },
    };
  }
  return getBaseQueryForUrl(baseUrl)(args, api, extraOptions);
};

let isRefreshing = false;
let pendingRequests: Array<{
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}> = [];

const flushQueue = (error?: unknown) => {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(undefined);
  });
  pendingRequests = [];
};

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const fresh = await ensureAccessTokenFresh();
  if (!fresh) {
    logoutSession();
    return {
      error: {
        status: 401,
        data: { message: "Session expired. Please log in again." },
      },
    };
  }

  const result = await dynamicBaseQuery(args, api, extraOptions);

  const errorCode = (result.error?.data as { error?: { code?: string } })?.error
    ?.code;
  // 403 = Forbidden (permission denied) — do NOT refresh on 403, only on 401.
  const isAuthError =
    result.error?.status === 401 ||
    errorCode === "INVALID_TOKEN" ||
    errorCode === "AUTHENTICATION_FAILED";

  if (!isAuthError) return result;

  const endpoint =
    typeof args === "string"
      ? args
      : ((args as { url?: string })?.url ?? "unknown");
  console.log(
    `[Auth] Auth error on "${endpoint}" | status: ${result.error?.status} | code: ${errorCode ?? "none"}`,
  );

  // Concurrent auth error: queue behind the in-flight refresh
  if (isRefreshing) {
    console.log(`[Auth] Refresh already in progress — queuing "${endpoint}"`);
    return new Promise((resolve, reject) => {
      pendingRequests.push({ resolve, reject });
    })
      .then(() => {
        console.log(`[Auth] Retrying queued request: "${endpoint}"`);
        return dynamicBaseQuery(args, api, extraOptions);
      })
      .catch(() => result);
  }

  // First 401: own the refresh
  console.log("[Auth] Starting token refresh...");
  isRefreshing = true;
  const refreshed = await refreshAccessToken();
  isRefreshing = false;

  if (refreshed) {
    console.log(
      `[Auth] Refresh succeeded — retrying original request : "${endpoint}"`,
    );
    flushQueue();
    return dynamicBaseQuery(args, api, extraOptions);
  }

  console.error(
    "[Auth] Refresh failed — flushing queue with error and logging out",
  );
  flushQueue(new Error("Token refresh failed"));
  logoutSession();
  return result;
};

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Roles",
    "Permissions",
    "Members",
    "Groups",
    "Patients",
    "Assessments",
    "Profile",
    "Sessions",
    "Languages",
  ],
  endpoints: () => ({}),
});
